'use client'

import AppFormState from "@/app/components/atomic/AppFormState";
import AppInput from "@/app/components/atomic/Input/AppInput";
import AppTextInput from "@/app/components/atomic/Input/AppTextInput";
import AppButton from "@/app/components/atomic/molecules/AppButton";
import AppCheckbox from "@/app/components/atomic/Input/AppCheckbox";
import ContactConsentCheckboxes from "@/app/components/atomic/molecules/ContactConsentCheckboxes";
import {usePhoneConsentMode} from "@/app/hooks/usePhoneConsentMode";
import Modal, {IModal} from "@/app/components/pageModels/healthLanding/components/Modal";
import {useTracking} from "@/app/contexts/tracking-context";
import {CallbackTrackingEvents} from "@/app/constants/funnelTracking";
import {Products} from "@/app/enums/Products";
import yupLocalized from "@/app/lib/Yup/yup-localized";
import {useHealthLandingStore} from "@/app/stores/health/health-landing-store";
import {useLoanLandingStore} from "@/app/stores/loan/loan-landing-store";
import {usePopupNotificationStore} from "@/app/stores/popup-notification-store";
import {useToastStore} from "@/app/stores/toast-store";
import {CallbackAlreadyPlannedError} from "@/app/endpoints/commonEndpoints";
import {pendingCallbackNotice} from "@/app/utils/pending-callback";
import {setCrossDomainCookie} from "@/app/utils/client-cookies";
import {areNamesIdentical, validateName} from "@/app/utils/name-validation-utils";
import OpenHourToolkit from "@/app/utils/open-hour-toolkit";
import { useLeadConversionTracking } from "@/app/hooks/useLeadConversionTracking";
import {CityInfo, validatePostcode} from "@/app/utils/validation-utils";
import {yupResolver} from '@hookform/resolvers/yup';
import dynamic from "next/dynamic";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {UseFormReturn} from "react-hook-form";
import {useShallow} from "zustand/react/shallow";
import {ThemeCocoon} from "@/app/constants/theme";
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon";

interface AvailableSlot {
    value: number;
    label: string;
    available: boolean;
}

const AppIcon = dynamic(() => import('@/app/components/atomic/AppIcon'));

interface ICallbackModal extends IModal {
    productType: ThemeCocoon;
    fetchSlotsFromApi?: boolean;
}

// TODO: Check how to change default strings for common errors
const createSchema = (isHealthProduct: boolean, phoneConsentMode: boolean) => yupLocalized.object({
    lastName: yupLocalized.string()
        .required()
        .min(2, 'Le nom doit contenir au moins 2 caractères')
        .max(30, 'Le nom doit contenir au maximum 30 caractères')
        .test('name-validation', function (value) {
            if (!value) return true;
            const error = validateName(value);
            if (error) return this.createError({message: error});
            return true;
        })
    ,
    firstName: yupLocalized.string().required()
        .min(2, 'Le prénom doit contenir au moins 2 caractères')
        .max(30, 'Le prénom doit contenir au maximum 30 caractères')
        .test('name-validation', function (value) {
            if (!value) return true;
            const error = validateName(value);
            if (error) return this.createError({message: error});
            return true;
        })
        .test('names-identical', 'Les noms et prénoms sont identiques', function (value) {
            if (!value) return true;
            const {lastName} = this.parent;
            if (!lastName) return true;
            return !areNamesIdentical(value, lastName);
        })
    ,
    email: yupLocalized.string().required().email(),
    phone: yupLocalized.string()
        .required()
        .min(14, 'Le numéro de téléphone doit contenir au moins 10 caractères')
        .max(14, 'Le numéro de téléphone doit contenir au maximum 10 caractères')
        .matches(/^(0)[1-9]( [0-9]{2}){4}$/, 'Le numéro de téléphone n\'est pas valide')
    ,
    zipcode: isHealthProduct
        ? yupLocalized.string()
            .required('Le code postal est requis')
            .min(5, 'Le code postal doit contenir 5 chiffres')
            .max(5, 'Le code postal doit contenir 5 chiffres')
            .matches(/^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/, 'Le code postal n\'est pas valide')
            .test('postcode-validation', async function (value) {
                if (!value || value.length !== 5) return true; // Let other validators handle this
                const result = await validatePostcode(value);
                if (!result.valid) {
                    return this.createError({
                        message: result.message || 'Le code postal est invalide'
                    });
                }
                return true;
            })
        : yupLocalized.string().notRequired()
    ,
    city: isHealthProduct
        ? yupLocalized.string()
            .required('La ville est requise')
            .min(2, 'La ville doit contenir au moins 2 caractères')
            .max(50, 'La ville doit contenir au maximum 50 caractères')
            .matches(/^[a-zA-ZÀ-ÿ0-9\s\-']*$/, 'La ville n\'est pas valide')
        : yupLocalized.string().notRequired()
    ,
    date: yupLocalized.string()
        .required()
        .min(10, 'La date doit être valide')
        // today or later
        .test('date', 'La date ne peut etre dans le passé', (value) => {
            // is date before today or today has no bookable slot left
            const todayFormatted = OpenHourToolkit.formatDateToYYYYMMDD();
            const isTodayValidation = todayFormatted === value
            const hour = new Date().getHours() + 1
            const todayIsOver = isHealthProduct
                ? hour > 17
                : OpenHourToolkit.minSelectableSlotValue() > OpenHourToolkit.LAST_SLOT_VALUE

            if (isTodayValidation && todayIsOver) return false

            // Create dates with noon time to avoid timezone issues
            const valueDate = new Date(value);
            valueDate.setHours(12, 0, 0, 0);

            const todayDate = new Date(todayFormatted);
            todayDate.setHours(12, 0, 0, 0);

            return valueDate >= todayDate;
        })
    ,
    hours: yupLocalized.string().required(),
    ...(phoneConsentMode
        ? {
            acceptPhoneContact: yupLocalized.boolean()
                .required('Le consentement téléphonique est requis pour être rappelé')
                .oneOf([true], 'Le consentement téléphonique est requis pour être rappelé'),
            acceptEmailContact: yupLocalized.boolean().notRequired(),
        }
        : {
            legalConsent: yupLocalized.boolean()
                .required('Vous devez accepter les conditions pour continuer')
                .oneOf([true], 'Vous devez accepter les conditions pour continuer'),
        }),
})

export default function CallbackModal(props: ICallbackModal) {
    const {addToast} = useToastStore()
    const {showNotification} = usePopupNotificationStore()
    const {track, trackLead} = useTracking();
    const trackLeadConversion = useLeadConversionTracking();
    const productType = props.productType || Products.HEALTH;
    const isHealth = productType === Products.HEALTH;
    const phoneConsentMode = usePhoneConsentMode();

    // Health store data
    const {
        setProperty: healthSetProperty,
        token: healthToken,
        createQuote: healthCreateQuote,
        askForAppointment: healthAskForAppointment,
        isCallbackFormLoading: healthIsCallbackFormLoading
    } = useHealthLandingStore(useShallow((state) => ({
        setProperty: state.setProperty,
        token: state.token,
        createQuote: state.createQuote,
        askForAppointment: state.askForAppointment,
        isCallbackFormLoading: state.isCallbackFormLoading
    })));

    // Loan store data
    const {
        setProperty: loanSetProperty,
        token: loanToken,
        createQuote: loanCreateQuote,
        askForAppointment: loanAskForAppointment,
        isCallbackFormLoading: loanIsCallbackFormLoading
    } = useLoanLandingStore(useShallow((state) => ({
        setProperty: state.setProperty,
        token: state.token,
        createQuote: state.createQuote,
        askForAppointment: state.askForAppointment,
        isCallbackFormLoading: state.isCallbackFormLoading || false
    })));

    // Use the appropriate store values based on product type
    const currentSale = isHealth ? {name: "un expert"} : {name: "Tanguy M.", expert_id: 0};
    const setProperty = isHealth ? healthSetProperty : loanSetProperty;
    const token = isHealth ? healthToken : loanToken;
    const createQuote = isHealth ? healthCreateQuote : loanCreateQuote;
    const askForAppointment = isHealth ? healthAskForAppointment : loanAskForAppointment;
    const isCallbackFormLoading = isHealth ? healthIsCallbackFormLoading : loanIsCallbackFormLoading;

    // State for postcode validation
    const [isValidatingPostcode, setIsValidatingPostcode] = useState(false);
    const [availableCities, setAvailableCities] = useState<CityInfo[]>([]);
    const [showCityList, setShowCityList] = useState(false);
    const [isLegalExpanded, setIsLegalExpanded] = useState(false);

    // Ref to store form methods for triggering validation
    const formMethodsRef = useRef<UseFormReturn | null>(null);

    // Ref for city selection list
    const cityListRef = useRef<HTMLDivElement>(null);

    // Smooth scroll to city list when it appears
    useEffect(() => {
        if (showCityList && cityListRef.current) {
            cityListRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [showCityList]);

    // La modale est montée à l'ouverture : son mount est l'affichage du formulaire de rappel.
    useEffect(() => {
        track({event: CallbackTrackingEvents.accessRequest});
    }, [track]);

    const getStoreState = useCallback(() => {
        return isHealth
            ? useHealthLandingStore.getState()
            : useLoanLandingStore.getState()
    }, [isHealth])

    // Handler for postcode validation and city auto-fill
    const handlePostcodeChange = useCallback(async (postcode: string) => {
        setProperty('zipcode', postcode);

        setShowCityList(false);
        setAvailableCities([]);
        setProperty('city', '');
        formMethodsRef.current?.setValue('city', '', {shouldValidate: false});

        if (postcode && postcode.length === 5 && /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/.test(postcode)) {
            setIsValidatingPostcode(true);
            const result = await validatePostcode(postcode);
            setIsValidatingPostcode(false);

            if (result.valid) {
                if (result.cities && result.cities.length > 1) {
                    setAvailableCities(result.cities);
                    setShowCityList(true);
                } else if (result.city) {
                    setProperty('city', result.city);
                    setTimeout(() => {
                        formMethodsRef.current?.setValue('city', result.city, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true
                        });
                    }, 100);
                }
            }
        }
    }, [setProperty]);

    const selectCity = useCallback((city: CityInfo) => {
        setProperty('city', city.name);
        setShowCityList(false);
        setAvailableCities([]);
        setTimeout(() => {
            formMethodsRef.current?.setValue('city', city.name, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
        }, 100);
    }, [setProperty]);

    const methods = useMemo(() => ({
        mode: 'onTouched',
        resolver: yupResolver(createSchema(isHealth, phoneConsentMode)),
        shouldFocusError: true,
    }), [isHealth, phoneConsentMode])

    // The store omits the consents from the callback payload when the mode is off
    useEffect(() => {
        setProperty('phoneConsentMode', phoneConsentMode);
    }, [setProperty, phoneConsentMode]);

    useEffect(() => {
        const date = getStoreState().callbackForm.date;
        const dateOrToday = date || OpenHourToolkit.formatDateToYYYYMMDD()

        if (!OpenHourToolkit.isToday(dateOrToday)) return

        if (isHealth) {
            if (OpenHourToolkit.isHourAbove17()) {
                setProperty('date', OpenHourToolkit.getNextOpenDate(date as string))
            }
        } else if (OpenHourToolkit.minSelectableSlotValue() > OpenHourToolkit.LAST_SLOT_VALUE) {
            setProperty('date', OpenHourToolkit.minSelectableDate())
        }
    }, []);

    const handleSuccess = useCallback(async (tokenToSave?: string | null) => {
        if (tokenToSave && isHealth) {
            setCrossDomainCookie('health_quote_stored_token', tokenToSave);

            const currentPath = window.location.pathname;
            if (!currentPath.includes(tokenToSave)) {
                const newPath = currentPath.endsWith('/')
                    ? `${currentPath}${tokenToSave}`
                    : `${currentPath}/${tokenToSave}`;
                window.history.pushState({}, '', newPath);
            }
        }

        props.closeModal?.()

        showNotification({
            text: 'rappel',
            image: 'WeeDoIT'
        });

        const callbackForm = getStoreState().callbackForm;

        // Conversion lead sur les 3 canaux (Google Ads / Meta CAPI / TikTok Events API).
        trackLeadConversion({
            googleEvent: 'conversion',
            productType,
            fbEventName: isHealth ? 'MS - Callback requested (API)' : 'Callback requested (API)',
            email: callbackForm.email,
            phone: callbackForm.phone,
            token: getStoreState().token,
        });

        track({
            event: 'gtm_callback_modal_validated',
            productType,
        });

        void trackLead(CallbackTrackingEvents.requestValidated, {
            email: callbackForm.email,
            phone: callbackForm.phone,
        });
    }, [isHealth, productType, props.closeModal, showNotification, track, trackLead, trackLeadConversion, getStoreState])

    const handleCallback = useCallback(async (formMethods: UseFormReturn) => {
        const storeState = getStoreState().callbackForm;

        Object.entries(storeState).forEach(([key, value]) => {
            if (value !== undefined) {
                formMethods.setValue(key, value, {shouldValidate: false});
            }
        });

        await formMethods.trigger()
        const {formState} = formMethods

        if (Object.keys(formState.errors).length > 0) {
            const firstError = (Object.values(formState.errors) as any)[0]
            const focusTarget = firstError?.ref?.name ?? Object.keys(formState.errors)[0]
            if (focusTarget) formMethods.setFocus(focusTarget)
            return
        }

        try {
            const success = token ? await askForAppointment() : await createQuote(true)

            if (success) {
                await handleSuccess(token || getStoreState().token)
            } else {
                addToast('Une erreur est survenue, veuillez réessayer plus tard', 'error')
            }
        } catch (error) {
            if (error instanceof CallbackAlreadyPlannedError) {
                addToast(pendingCallbackNotice(error.pending), 'error')
            } else {
                addToast('Une erreur est survenue, veuillez réessayer plus tard', 'error')
            }
        }
    }, [token, createQuote, askForAppointment, addToast, handleSuccess, getStoreState])

    return <Modal {...props}>
        <Modal.Title productType={productType}>
            Prendre {' '}<span className="hidden md:inline-flex">rendez-vous</span>
            <span className="md:hidden">rdv</span>{' '} avec {currentSale.name}
        </Modal.Title>

        <Modal.Paragraph className={"my-4"}>
            {productType === Products.LOAN_INSURANCE && (
                <>
                    votre conseiller <span className="hidden md:inline-flex">dédié</span> en assurance de prêt
                </>
            )}
            {productType === Products.HEALTH && (
                <>
                    Nos experts sont disponibles pour vous conseiller et vous accompagner du lundi au vendredi de 09h00 à 18h00.
                </>
            )}
        </Modal.Paragraph>

        <AppFormState
            formMethods={methods}
            store={isHealth ? useHealthLandingStore : useLoanLandingStore}
            storeFormData={'callbackForm'}
            formClasses={'flex flex-col flex-1'}
        >
            {((formMethods: UseFormReturn, formData: any) => {
                // Store form methods in ref for use in handlers
                formMethodsRef.current = formMethods;

                return (<>
                    <div className={"flex gap-5 flex-col"}>
                        <div className={"flex justify-between gap-3 max-rm-md:flex-col"}>
                            <div data-test="callback-lastname-field" className="flex-1 w-full">
                                <AppFormState.Label>Nom <Asterisk /></AppFormState.Label>
                                <AppTextInput
                                    placeholder={"Doe"}
                                    validation={true}
                                    fieldName={"lastName"}
                                    value={formData?.lastName}
                                    borders='neutral'
                                    onChange={(inputReturn) => {
                                        setProperty('lastName', inputReturn.value);
                                    }}
                                />
                            </div>
                            <div data-test="callback-firstname-field" className="flex-1 w-full">
                                <AppFormState.Label>Prénom <Asterisk /></AppFormState.Label>
                                <AppTextInput
                                    placeholder={"John"}
                                    validation={true}
                                    fieldName={"firstName"}
                                    value={formData?.firstName}
                                    addonTheme={"blank"}
                                    borders='neutral'
                                    onChange={(inputReturn) => {
                                        setProperty('firstName', inputReturn.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div data-test="callback-email-field">
                            <AppFormState.Label>E-mail <Asterisk /></AppFormState.Label>
                            <AppTextInput
                                placeholder={'john.doe@gmail.com'}
                                value={formData?.email}
                                fieldName={"email"}
                                validation={true}
                                addonTheme={"blank"}
                                className="w-full"
                                borders='neutral'
                                onChange={(inputReturn) => {
                                    setProperty('email', inputReturn.value);
                                }}
                            />
                        </div>
                        <div data-test="callback-phone-field">
                            <AppFormState.Label>Téléphone <Asterisk /></AppFormState.Label>
                            <AppTextInput
                                placeholder={'06 10 20 30 40'}
                                mask="__ __ __ __ __"
                                replacement={{_: /\d/}}
                                value={formData?.phone}
                                fieldName={"phone"}
                                addonTheme={"blank"}
                                className="w-full"
                                validation={true}
                                borders='neutral'
                                onChange={(inputReturn) => {
                                    setProperty('phone', inputReturn.value);
                                }}
                            />
                        </div>
                        {productType === Products.HEALTH && (
                            <div className={"flex justify-between gap-3 max-rm-md:flex-col"}>
                                <div data-test="callback-zipcode-field" className="flex-1 w-full">
                                    <AppFormState.Label>
                                        Code Postal <Asterisk />
                                        {isValidatingPostcode && (
                                            <span className="ml-2 text-pale text-xs">
                                                <AppIcon type={"regular"} iconClassName={"fa-spinner fa-spin"} />
                                                <span className="ml-1">Vérification...</span>
                                            </span>
                                        )}
                                    </AppFormState.Label>
                                    <AppTextInput
                                        placeholder={'75018'}
                                        mask="_____"
                                        replacement={{_: /\d/}}
                                        value={formData?.zipcode}
                                        fieldName={"zipcode"}
                                        addonTheme={"blank"}
                                        className="w-full"
                                        validation={true}
                                        borders='neutral'
                                        onChange={(inputReturn) => {
                                            handlePostcodeChange(inputReturn.value);
                                        }}
                                    />
                                </div>
                                <div data-test="callback-city-field" className="flex-1 w-full">
                                    <AppFormState.Label>Ville <Asterisk /></AppFormState.Label>
                                    <AppTextInput
                                        placeholder={'Paris'}
                                        value={formData?.city}
                                        fieldName={"city"}
                                        addonTheme={"blank"}
                                        disabled
                                        noDisabledBorder
                                        className="w-full bg-white cursor-not-allowed"
                                        validation={true}
                                        borders='neutral'
                                        inputProps={{
                                            readOnly: true,
                                            tabIndex: -1
                                        }}
                                        onChange={() => {
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* City Selection Buttons */}
                        {showCityList && availableCities.length > 0 && (
                            <div ref={cityListRef} className="mb-5 p-4 border border-rm-gray-light rounded bg-rm-gray-light/30" data-test="callback-city-selection">
                                <p className="text-sm font-semibold text-pale mb-3">
                                    Plusieurs villes correspondent à ce code postal. Veuillez sélectionner votre ville :
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {availableCities.map((city, index) => (
                                        <button
                                            key={`city-${index}`}
                                            type="button"
                                            className="px-4 py-2 bg-white border-2 border-rm-gray
                                                   hover:bg-rm-gray/50 hover:text-white transition-colors duration-200
                                                   font-medium text-sm"
                                            onClick={() => selectCity(city)}
                                        >
                                            {city.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className={"flex justify-between gap-3 max-rm-md:flex-col"}>
                            <div className={"flex-1 w-full"} data-test="callback-date-field">
                                <AppFormState.Label>Date <Asterisk /></AppFormState.Label>
                                <AppInput
                                    addonTheme={"blank"}
                                    type={"date"}
                                    fieldName={"date"}
                                    validation={true}
                                    borders="neutral"
                                    wrapperClass="pl-4"
                                    inputProps={{
                                        minDate: !isHealth
                                            ? OpenHourToolkit.minSelectableDate()
                                            : OpenHourToolkit.isHourAbove17()
                                                ? OpenHourToolkit.getNextOpenDate()
                                                : OpenHourToolkit.formatDateToYYYYMMDD()
                                    }}
                                    value={formData?.date}
                                    onChange={(inputReturn) => {
                                        setProperty('date', inputReturn.value);
                                    }}
                                />
                            </div>
                            <div className={"flex-1 w-full"} data-test="callback-time-field">
                                <AppFormState.Label>Heure <Asterisk /></AppFormState.Label>
                                <HoursSelect formData={formData} setProperty={setProperty} fetchSlotsFromApi={props.fetchSlotsFromApi || false} isHealth={isHealth} />
                            </div>
                        </div>


                        {phoneConsentMode ? (
                            <div className="my-5" data-test="callback-contact-consents">
                                <ContactConsentCheckboxes
                                    variant={productType === Products.LOAN_INSURANCE ? "loanCallback" : "legacy"}
                                    acceptPhoneContact={formData?.acceptPhoneContact || false}
                                    acceptEmailContact={formData?.acceptEmailContact || false}
                                    onPhoneContactChange={(checked) => {
                                        setProperty('acceptPhoneContact', checked);
                                    }}
                                    onEmailContactChange={(checked) => {
                                        setProperty('acceptEmailContact', checked);
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="my-5 border border-rm-gray-light rounded p-rm-10" data-test="callback-legal-consent">
                                <AppCheckbox
                                    value={formData?.legalConsent || false}
                                    fieldName="legalConsent"
                                    onChange={(checked) => {
                                        setProperty('legalConsent', checked);
                                    }}
                                    inputClassName="h-[20px] w-[20px] min-w-[20px]"
                                    boxClassName="bg-rm-gray-light/20 border-rm-gray"
                                >
                                    <span className="text-xs text-pale">
                                        J&apos;accepte d&apos;être contacté(e) par email par Réassurez-moi afin de traiter ma demande et de me présenter les offres
                                        d&apos;assurance.
                                    </span>
                                </AppCheckbox>

                                {formMethods.formState.errors.legalConsent && (
                                    <p className="text-red-500 text-xs mt-1 ml-[50px]">
                                        {formMethods.formState.errors.legalConsent.message as string}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div
                        className="mt-auto md:sticky relative bottom-0 bg-white w-full py-3"
                    >
                        <AppButton
                            wFull
                            theme="orange"
                            size="lg"
                            arrowPosition="before"
                            disabled={isCallbackFormLoading || (phoneConsentMode ? !formData?.acceptPhoneContact : !formData?.legalConsent)}
                            dataTest="callback-submit-btn"
                            onClick={(e) => {
                                e?.preventDefault?.();
                                handleCallback(formMethods)
                            }}
                        >
                            {isCallbackFormLoading ?
                                <>
                                    <AppIcon type={"regular"} iconClassName={"fa-spinner fa-spin"} />
                                    <span className={"ml-2"}>Chargement...</span>
                                </>
                                :
                                <>
                                    Confirmer le rdv
                                </>
                            }
                        </AppButton>
                    </div>

                    <div className="mt-3">
                        <div className="bg-[#f5fafe] border border-[#b9d3ee] rounded-lg px-[10px] py-[20px]">
                            <div className="flex items-start gap-[15px] text-black">
                                <AppIcon iconClassName="fa-shield-halved" size="lg" className="mt-[2px] shrink-0 text-rm-blue" />
                                <div className="flex-1 flex flex-col gap-[10px]">
                                    <p className="text-[12px] font-semibold">Vos données personnelles</p>

                                    <p className={`text-left text-[11px] leading-4 ${!isLegalExpanded ? "line-clamp-3" : ""}`}>
                                        Les champs suivis d&rsquo;un astérisque* sont obligatoires.<br />
                                        Réassurez-moi collecte vos données personnelles pour analyser votre demande et
                                        permettre la souscription éventuelle d&rsquo;un contrat auprès de ses partenaires,
                                        sur la base de son intérêt légitime et/ou de mesures précontractuelles. Vous disposez
                                        d&rsquo;un droit d&rsquo;accès, de rectification, de suppression, d&rsquo;opposition
                                        et de limitation. Lorsque le traitement repose sur votre consentement, vous pouvez
                                        le retirer à tout moment. Vous pouvez exercer vos droits en vous adressant à
                                        dpo@reassurez-moi.fr ou à DPO, Réassurez-moi, 19 Rue d&apos;Aumale, 75009 Paris. Vos
                                        données seront transmises à nos partenaires pour poursuivre le traitement de votre
                                        demande. Pour en savoir plus sur la gestion de vos données personnelles, veuillez
                                        consulter notre politique de protection des données disponible ici :{" "}
                                        <a
                                            href="https://reassurez-moi.fr/guide/politique-confidentialite"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#184980] underline"
                                        >
                                            https://reassurez-moi.fr/guide/politique-confidentialite
                                        </a>
                                    </p>

                                    <button
                                        type="button"
                                        onClick={() => setIsLegalExpanded(!isLegalExpanded)}
                                        aria-expanded={isLegalExpanded}
                                        className="self-start flex items-center gap-[4px] text-[11px] underline underline-offset-2"
                                    >
                                        {isLegalExpanded ? "Réduire" : "Développer"}
                                        <ChevronIcon open={isLegalExpanded} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>);
            }) as any}
        </AppFormState>
    </Modal>
}


function HoursSelect(props: any) {
    const fieldName = 'hours'
    const {hours, date} = props.formData
    const [options, setOptions] = useState<AvailableSlot[]>([])

    useEffect(() => {
        const fetchHours = async () => {
            try {
                let _options: AvailableSlot[];

                if (props.fetchSlotsFromApi) {
                    _options = await OpenHourToolkit.getHoursForDay(date as string)
                } else {
                    const {START_HOUR, END_HOUR} = OpenHourToolkit
                    let min = START_HOUR

                    if (OpenHourToolkit.isToday(date)) {
                        min = props.isHealth
                            ? (new Date().getHours() * 100 + 30).toString()
                            : OpenHourToolkit.minSelectableSlotValue().toString()
                    }

                    if (props.isHealth && parseInt(min) >= parseInt(END_HOUR)) min = START_HOUR

                    const staticOptions = OpenHourToolkit.formatHours(min, END_HOUR)
                    _options = staticOptions.map(slot => ({
                        value: typeof slot.value === 'string' ? parseInt(slot.value) : slot.value,
                        label: slot.label,
                        available: true
                    }))
                }

                setOptions(_options)

                // Find the first available option and set it as default
                const firstAvailableOption = _options.find(option => option.available !== false)
                if (firstAvailableOption) {
                    props.setProperty(fieldName, firstAvailableOption.value)
                }
            } catch (error) {
                console.error('Error fetching hours:', error)
                setOptions([])
            }
        }

        if (date) {
            fetchHours()
        }
    }, [date, props.fetchSlotsFromApi, props.isHealth]);

    return <AppInput
        fieldName={fieldName}
        value={hours}
        type={"select"}
        inputProps={{options}}
        addonTheme={"blank"}
        borders="neutral"
        wrapperClass="pl-4"
        onChange={(inputReturn) => {
            props.setProperty(fieldName, inputReturn.value);
        }}
    />
}

function Asterisk() {
    return <span className="text-deep-orange">*</span>
}
